// frontend/src/components/admin/ContratManager.jsx

import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, ChevronUp, ChevronDown, Save, FileText,
  CheckCircle2, AlertCircle, RefreshCw, Edit3, X, Eye,
  Copy, Building2, User, MapPin, CreditCard, BookOpen,
  ArrowLeft, FileCheck, Send,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const Field = ({ label, value, onChange, placeholder, icon: Icon, half, theme: t }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />}
      <input
        type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%', padding: Icon ? '8px 10px 8px 30px' : '8px 10px',
          borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`,
          color: t.text, fontSize: 12, fontWeight: 500, boxSizing: 'border-box', outline: 'none',
        }}
        onFocus={e => { e.target.style.borderColor = t.primary; }}
        onBlur={e =>  { e.target.style.borderColor = t.border; }}
      />
    </div>
  </div>
);

const NumberField = ({ label, value, onChange, suffix, half, theme: t }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
      {label}
    </label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number" min={0} value={value || 0} onChange={e => onChange(parseFloat(e.target.value) || 0)}
        style={{
          flex: 1, padding: '8px 10px', borderRadius: 8, background: t.bgInput,
          border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none',
        }}
        onFocus={e => { e.target.style.borderColor = t.primary; }}
        onBlur={e =>  { e.target.style.borderColor = t.border; }}
      />
      {suffix && <span style={{ fontSize: 11, color: t.textMuted, whiteSpace: 'nowrap' }}>{suffix}</span>}
    </div>
  </div>
);

// ── Carte d'un article éditable ──────────────────────────────────────────────
const ArticleCard = ({ article, index, total, onChange, onRemove, onMoveUp, onMoveDown, theme: t }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{
      borderRadius: 12, background: t.bgCard, border: `1px solid ${t.border}`,
      overflow: 'hidden', marginBottom: 10,
    }}>
      {/* Barre titre */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
        background: t.bgInput, borderBottom: expanded ? `1px solid ${t.border}` : 'none',
        cursor: 'pointer',
      }} onClick={() => setExpanded(e => !e)}>
        <span style={{ width: 22, height: 22, borderRadius: 6, background: t.primarySoft, color: t.primary,
          fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {index + 1}
        </span>
        <input
          type="text"
          value={article.titre}
          onChange={e => { e.stopPropagation(); onChange({ ...article, titre: e.target.value }); }}
          onClick={e => e.stopPropagation()}
          placeholder="Titre de l'article..."
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            color: t.text, fontSize: 12, fontWeight: 700, cursor: 'text',
          }}
        />
        {/* Contrôles */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onMoveUp()} disabled={index === 0}
            style={{ ...btnIconStyle(t), opacity: index === 0 ? 0.3 : 1 }}>
            <ChevronUp size={13} />
          </button>
          <button onClick={() => onMoveDown()} disabled={index === total - 1}
            style={{ ...btnIconStyle(t), opacity: index === total - 1 ? 0.3 : 1 }}>
            <ChevronDown size={13} />
          </button>
          <button onClick={() => onRemove()}
            style={{ ...btnIconStyle(t), color: '#ef4444' }}>
            <Trash2 size={13} />
          </button>
          <button onClick={() => setExpanded(e => !e)} style={btnIconStyle(t)}>
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {/* Contenu */}
      {expanded && (
        <div style={{ padding: 14 }}>
          <textarea
            value={article.contenu}
            onChange={e => onChange({ ...article, contenu: e.target.value })}
            rows={6}
            placeholder="Contenu de l'article...&#10;&#10;Utilisez {{frais_mise_en_place}}, {{montant_mensuel_base}}, {{montant_final}} pour insérer des valeurs dynamiques."
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              background: t.bgInput, border: `1px solid ${t.border}`,
              color: t.text, fontSize: 12, lineHeight: 1.7, resize: 'vertical',
              fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = t.primary; }}
            onBlur={e =>  { e.target.style.borderColor = t.border; }}
          />
          <p style={{ fontSize: 10, color: t.textMuted, margin: '5px 0 0' }}>
            Variables : <code style={{ color: t.primary }}>{'{{frais_mise_en_place}}'}</code> &nbsp;
            <code style={{ color: t.primary }}>{'{{montant_mensuel_base}}'}</code> &nbsp;
            <code style={{ color: t.primary }}>{'{{montant_final}}'}</code> &nbsp;
            <code style={{ color: t.primary }}>{'{{client_nom}}'}</code>
          </p>
        </div>
      )}
    </div>
  );
};

const btnIconStyle = (t) => ({
  width: 26, height: 26, borderRadius: 6, border: `1px solid ${t.border}`,
  background: t.bgCard, color: t.textMuted, cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: 0,
});

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const ContratManager = ({ initialContratId = null, onNavigate }) => {
  const t = useTheme();

  // ── State global ──
  const [view, setView] = useState('list');     // 'list' | 'editor' | 'new'
  const [contrats, setContrats]     = useState([]);
  const [templates, setTemplates]   = useState([]);
  const [prospects, setProspects]   = useState([]);
  const [companies, setCompanies]   = useState([]);
  const [loading,   setLoading]     = useState(true);
  const [saving,    setSaving]      = useState(false);
  const [error,     setError]       = useState('');
  const [success,   setSuccess]     = useState('');

  // ── State éditeur ──
  const [editId,         setEditId]         = useState(null);
  const [articles,       setArticles]       = useState([]);
  const [paymentMethods,  setPaymentMethods]  = useState([]);
  const [extensionsDispo, setExtensionsDispo] = useState([]);
  const [licencePlans,    setLicencePlans]    = useState({});
  const [meta, setMeta] = useState({
    client_nom:          '',
    client_representant: '',
    client_ville:        '',
    type_licence:        '',
    plan_label:          '',
    frais_mise_en_place: 50000,
    montant_mensuel:     0,
    discount_type:       'percent',
    discount_value:      0,
    montant_final:       0,
    jour_paiement:       15,
    payment_method_id:   '',
    addon_employes:      0,
    addon_utilisateurs:  0,
    extensions_devis:    '[]', // toujours initialisé
    lieu_signature:      'Pointe-Noire, République Du Congo',
    date_signature:      new Date().toISOString().slice(0, 10),
    duree_mois:          12,
    notes:               '',
    source:              'prospect',
    source_id:           '',
    devis_id:            '',
  });

  // ── Charger données ──
  useEffect(() => {
    loadAll();
  }, []);

  // Ouvrir directement l'éditeur si on arrive depuis un devis
  useEffect(() => {
    if (!initialContratId || !contrats.length) return;
    const contrat = contrats.find(c => c.id === initialContratId);
    if (contrat) handleEdit(contrat);
  }, [initialContratId, contrats, paymentMethods]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, tRes, pRes, coRes, pmRes, extRes, plansRes] = await Promise.all([
        superAdminApi.contrats.getAll(),
        superAdminApi.contrats.getTemplates(),
        superAdminApi.prospects.getAll(),
        superAdminApi.companies.getAll(),
        superAdminApi.danicedSettings.getPaymentMethods(),
        superAdminApi.extensions.getAll(),
        superAdminApi.licences.getPlans(),
      ]);
      if (cRes.success)  setContrats(cRes.data  || []);
      if (tRes.success)  setTemplates(tRes.data  || []);
      if (pRes.success)  setProspects(pRes.data  || []);
      if (coRes.success) setCompanies(coRes.data || []);
      if (pmRes.success)   setPaymentMethods((pmRes.data || []).filter(m => m.actif == 1));
      if (extRes?.success)   setExtensionsDispo(extRes.data  || []);
      if (plansRes?.success) setLicencePlans(plansRes.plans   || {});

      // initialContratId sera traité dans un useEffect dédié après mise à jour du state
    } catch (e) { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  // ── Nouveau contrat : charger le modèle de base ──
  const handleNewContrat = () => {
    setEditId(null);
    const firstPm = paymentMethods.length > 0 ? String(paymentMethods[0].id) : '';
    setMeta({
      client_nom: '', client_representant: '', client_ville: '',
      type_licence: '', plan_label: '',
      frais_mise_en_place: 50000, montant_mensuel: 0, discount_type: 'percent',
      discount_value: 0, montant_final: 0,
      jour_paiement: 15, payment_method_id: firstPm,
      lieu_signature: 'Pointe-Noire, République Du Congo',
      date_signature: new Date().toISOString().slice(0, 10),
      duree_mois: 12, notes: '', source: 'prospect', source_id: '', devis_id: '',
    });
    // Charger articles depuis le modèle de base
    setArticles(templates.map(tpl => ({ titre: tpl.titre, contenu: tpl.contenu })));
    setView('editor');
  };

  // ── Éditer un contrat existant ──
  const handleEdit = (contrat) => {
    setEditId(contrat.id);
    setMeta({
      client_nom:          contrat.client_nom          || '',
      client_representant: contrat.client_representant || '',
      client_ville:        contrat.client_ville        || '',
      type_licence:        contrat.type_licence        || '',
      plan_label:          contrat.plan_label          || '',
      frais_mise_en_place: parseFloat(contrat.frais_mise_en_place) || 0,
      montant_mensuel:     parseFloat(contrat.montant_mensuel)  || 0,
      discount_type:       contrat.discount_type       || 'percent',
      discount_value:      parseFloat(contrat.discount_value)   || 0,
      montant_final:       parseFloat(contrat.montant_final)    || 0,
      lieu_signature:      contrat.lieu_signature      || 'Pointe-Noire, République Du Congo',
      date_signature:      contrat.date_signature      ? contrat.date_signature.slice(0, 10) : new Date().toISOString().slice(0, 10),
      duree_mois:          contrat.duree_mois          || 12,
      jour_paiement:       contrat.jour_paiement       || 15,
      payment_method_id:   contrat.payment_method_id   ? String(contrat.payment_method_id) : (paymentMethods.length > 0 ? String(paymentMethods[0].id) : ''),
      addon_employes:      parseInt(contrat.addon_employes)     || 0,
      addon_utilisateurs:  parseInt(contrat.addon_utilisateurs) || 0,
      extensions_devis:    (contrat.extensions_devis && contrat.extensions_devis !== 'null') ? contrat.extensions_devis : '[]',
      notes:               contrat.notes               || '',
      source:    contrat.prospect_id ? 'prospect' : (contrat.company_id ? 'company' : 'direct'),
      source_id: contrat.prospect_id ? String(contrat.prospect_id) : (contrat.company_id ? String(contrat.company_id) : ''),
      devis_id:  contrat.devis_id || '',
    });
    setArticles(Array.isArray(contrat.articles) ? contrat.articles : []);
    setView('editor');
  };

  // ── Pré-remplir depuis prospect ──
  const handleSourceChange = async (source, sourceId) => {
    setMeta(m => ({ ...m, source, source_id: sourceId }));
    if (!sourceId) return;

    if (source === 'prospect') {
      const p = prospects.find(p => p.id === parseInt(sourceId));
      if (p) setMeta(m => ({ ...m, client_nom: p.nom, client_representant: p.contact || '', client_ville: p.ville || '' }));
    } else if (source === 'company') {
      const c = companies.find(c => c.id === parseInt(sourceId));
      if (c) setMeta(m => ({ ...m, client_nom: c.company_name, client_representant: c.contact_nom || '', client_ville: c.ville || '' }));
    }
  };

  // ── Ajouter un article vide ──
  const addArticle = (fromTemplate = null) => {
    if (fromTemplate) {
      setArticles(a => [...a, { titre: fromTemplate.titre, contenu: fromTemplate.contenu }]);
    } else {
      setArticles(a => [...a, { titre: '', contenu: '' }]);
    }
  };

  // ── Modifier un article ──
  const updateArticle = (index, updated) => {
    setArticles(a => a.map((item, i) => i === index ? updated : item));
  };

  // ── Supprimer un article ──
  const removeArticle = (index) => {
    setArticles(a => a.filter((_, i) => i !== index));
  };

  // ── Monter/Descendre ──
  const moveArticle = (index, dir) => {
    setArticles(a => {
      const arr  = [...a];
      const swap = index + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[index], arr[swap]] = [arr[swap], arr[index]];
      return arr;
    });
  };

  // ── Sauvegarder ──
  const handleSave = async (andGenerate = false) => {
    if (!meta.client_nom.trim()) { setError('Le nom du client est requis'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload = {
        ...meta,
        articles,
        addon_employes:     meta.addon_employes     || 0,
        addon_utilisateurs: meta.addon_utilisateurs || 0,
        extensions_devis:   (meta.extensions_devis && meta.extensions_devis !== 'null' && meta.extensions_devis !== 'undefined') ? meta.extensions_devis : '[]',
        jour_paiement:      meta.jour_paiement      || 15,
        payment_method_id:  meta.payment_method_id  ? parseInt(meta.payment_method_id) : null,
      };
      let res;
      if (editId) {
        res = await superAdminApi.contrats.update(editId, payload);
      } else {
        res = await superAdminApi.contrats.create(payload);
        if (res.success) setEditId(res.data.id);
      }

      if (!res.success) { setError(res.message || 'Erreur'); return; }

      if (andGenerate) {
        const id = editId || res.data?.id;
        superAdminApi.contrats.getPdf(id);
      }

      setSuccess(andGenerate ? 'Sauvegardé et PDF généré !' : 'Contrat sauvegardé !');
      setTimeout(() => setSuccess(''), 2500);
      await loadAll();
    } catch (e) { setError('Erreur réseau'); }
    finally { setSaving(false); }
  };

  // ── Marquer signé ──
  const handleMarkSigne = async (id) => {
    try {
      await superAdminApi.contrats.markSigne(id);
      await loadAll();
    } catch { setError('Erreur'); }
  };

  const fmt = (n) => parseFloat(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' FCFA';

  const STATUT_CONFIG = {
    brouillon: { label: 'Brouillon', color: '#9ca3af', bg: '#f3f4f6' },
    envoye:    { label: 'Envoyé',    color: '#3b82f6', bg: '#eff6ff' },
    signe:     { label: 'Signé',     color: '#22c55e', bg: '#f0fdf4' },
    resilie:   { label: 'Résilié',   color: '#ef4444', bg: '#fef2f2' },
  };

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  // ============================================================================
  // VUE : LISTE DES CONTRATS
  // ============================================================================
  if (view === 'list') return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Contrats</h1>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>{contrats.length} contrat(s) généré(s)</p>
        </div>
        <button onClick={handleNewContrat} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          borderRadius: 10, background: t.primary, color: '#fff', border: 'none',
          fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
          <Plus size={15} /> Nouveau contrat
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8 }}>
          <AlertCircle size={14} style={{ color: '#ef4444' }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {contrats.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: t.textMuted }}>
          <FileText size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p style={{ fontSize: 14, fontWeight: 600 }}>Aucun contrat</p>
          <p style={{ fontSize: 12 }}>Cliquez sur "Nouveau contrat" pour commencer</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {contrats.map(c => {
            const sc = STATUT_CONFIG[c.statut] || STATUT_CONFIG.brouillon;
            return (
              <div key={c.id} style={{ padding: '14px 18px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Icône */}
                <div style={{ width: 40, height: 40, borderRadius: 10, background: t.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FileText size={18} style={{ color: t.primary }} />
                </div>
                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: 0 }}>{c.client_nom}</p>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: sc.bg, color: sc.color }}>{sc.label}</span>
                    {c.plan_label && <span style={{ fontSize: 10, color: t.textMuted, padding: '2px 8px', borderRadius: 99, background: t.bgInput }}>{c.plan_label}</span>}
                  </div>
                  <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                    {c.numero} · {fmt(c.montant_final)}/mois
                    {c.date_signature && ` · Signé le ${new Date(c.date_signature).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {c.statut !== 'signe' && (
                    <button onClick={() => handleMarkSigne(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                      <FileCheck size={13} /> Signé
                    </button>
                  )}
                  <button onClick={() => superAdminApi.contrats.getPdf(c.id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, background: t.primarySoft, color: t.primary, border: `1px solid ${t.primaryBorder}`, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    <Eye size={13} /> PDF
                  </button>
                  <button onClick={() => handleEdit(c)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    <Edit3 size={13} /> Éditer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );

  // ============================================================================
  // VUE : ÉDITEUR DE CONTRAT
  // ============================================================================
  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* BARRE DE NAVIGATION */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setView('list')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <ArrowLeft size={13} /> Retour
          </button>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: 0 }}>
              {editId ? `Édition : ${meta.client_nom || 'Contrat'}` : 'Nouveau contrat'}
            </h1>
            <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
              {articles.length} article(s)
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => handleSave(false)} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, background: t.bgInput, color: t.text, border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            <Save size={13} /> Sauvegarder
          </button>
          <button onClick={() => handleSave(true)} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, background: t.primary, color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving
              ? <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
              : <Eye size={13} />}
            Sauvegarder & PDF
          </button>
        </div>
      </div>

      {(error || success) && (
        <div style={{ padding: '10px 14px', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center', background: success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)', border: `1px solid ${success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
          {success ? <CheckCircle2 size={14} style={{ color: '#22c55e' }} /> : <AlertCircle size={14} style={{ color: '#ef4444' }} />}
          <span style={{ fontSize: 12, color: success ? '#22c55e' : '#ef4444' }}>{success || error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── PANNEAU GAUCHE : META + SOURCE ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* SOURCE */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <User size={14} style={{ color: t.primary }} /> Source client
            </p>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {[
                { value: 'prospect', label: 'Prospect' },
                { value: 'company',  label: 'Entreprise' },
                { value: 'direct',   label: 'Direct' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setMeta(m => ({ ...m, source: opt.value, source_id: '' }))}
                  style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: `1px solid ${meta.source === opt.value ? t.primary : t.border}`, background: meta.source === opt.value ? t.primarySoft : t.bgInput, color: meta.source === opt.value ? t.primary : t.textMuted, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  {opt.label}
                </button>
              ))}
            </div>
            {meta.source === 'prospect' && (
              <select value={meta.source_id} onChange={e => handleSourceChange('prospect', e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none' }}>
                <option value="">— Choisir un prospect —</option>
                {prospects.map(p => <option key={p.id} value={p.id}>{p.nom}{p.contact ? ` · ${p.contact}` : ''}</option>)}
              </select>
            )}
            {meta.source === 'company' && (
              <select value={meta.source_id} onChange={e => handleSourceChange('company', e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none' }}>
                <option value="">— Choisir une entreprise —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            )}
          </div>

          {/* INFOS CLIENT */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Building2 size={14} style={{ color: t.primary }} /> Informations client
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="Nom de l'entreprise *" value={meta.client_nom} onChange={v => setMeta(m => ({ ...m, client_nom: v }))} placeholder="LCC GROUP" theme={t} />
              <Field label="Représentant" value={meta.client_representant} onChange={v => setMeta(m => ({ ...m, client_representant: v }))} placeholder="Jean Dupont" icon={User} theme={t} />
              <Field label="Ville" value={meta.client_ville} onChange={v => setMeta(m => ({ ...m, client_ville: v }))} placeholder="Brazzaville" icon={MapPin} theme={t} />
            </div>
          </div>

          {/* CONDITIONS FINANCIÈRES — lecture seule depuis le devis */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <CreditCard size={14} style={{ color: t.primary }} /> Conditions financières
            </p>
            <p style={{ fontSize: 11, color: t.textMuted, margin: '0 0 12px', fontStyle: 'italic' }}>
              Données issues du devis — non modifiables
            </p>
            {(() => {
              const fmt = n => parseFloat(n||0).toLocaleString('fr-FR') + ' FCFA';
              let exts = [];
              try { exts = JSON.parse(meta.extensions_devis || '[]'); } catch {}
              const prixBase    = parseFloat(meta.montant_mensuel || 0);
              const dv          = parseFloat(meta.discount_value  || 0);
              let licFinal = prixBase;
              if (meta.discount_type === 'percent') licFinal = prixBase * (1 - dv/100);
              else if (meta.discount_type === 'fixed') licFinal = prixBase - dv;
              licFinal = Math.max(0, licFinal);
              const totalExts   = exts.reduce((s,e) => s + parseFloat(e.prix_mensuel||0), 0);
              const empAddon    = parseInt(meta.addon_employes)    || 0;
              const userAddon   = parseInt(meta.addon_utilisateurs)|| 0;
              const plan        = licencePlans[meta.type_licence]  || {};
              const prixParEmp  = parseFloat(plan.prix_par_employe_sup   || 2500);
              const prixParUser = parseFloat(plan.prix_par_utilisateur_sup || 3500);
              const totalAddons = (Math.ceil(empAddon/10||0)*prixParEmp) + (userAddon*prixParUser);
              const total       = licFinal + totalExts + totalAddons;

              const Row = ({ label, value, color, bold }) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '5px 0', borderBottom: `1px solid ${t.border}` }}>
                  <span style={{ fontSize: 11, color: color || t.textSecondary }}>{label}</span>
                  <span style={{ fontSize: 11, fontWeight: bold ? 800 : 600, color: color || t.text }}>{value}</span>
                </div>
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Row label={`Licence ${meta.plan_label || meta.type_licence || '—'}`} value={fmt(prixBase)} />
                  {dv > 0 && (
                    <Row label={`Réduction (${meta.discount_type === 'percent' ? dv + '%' : fmt(dv)})`}
                      value={`- ${fmt(meta.discount_type === 'percent' ? prixBase * dv/100 : dv)}`}
                      color="#ef4444" />
                  )}
                  {exts.map(e => (
                    <Row key={e.flag_key} label={`Extension — ${e.label}`} value={fmt(e.prix_mensuel)} color="#7c3aed" />
                  ))}
                  {empAddon > 0 && (
                    <Row label={`Add-on ${empAddon} employés sup. (${Math.ceil(empAddon/10)} tranche${Math.ceil(empAddon/10)>1?'s':''})`}
                      value={fmt(Math.ceil(empAddon/10)*prixParEmp)} color="#d97706" />
                  )}
                  {userAddon > 0 && (
                    <Row label={`Add-on ${userAddon} utilisateur${userAddon>1?'s':''} sup.`}
                      value={fmt(userAddon*prixParUser)} color="#d97706" />
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px',
                    borderTop: `2px solid ${t.primary}`, marginTop: 2 }}>
                    <span style={{ fontSize: 13, fontWeight: 900, color: t.primary }}>Total mensuel</span>
                    <span style={{ fontSize: 13, fontWeight: 900, color: t.primary }}>{fmt(total)}</span>
                  </div>
                  {parseFloat(meta.frais_mise_en_place||0) > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0',
                      borderTop: `1px dashed ${t.border}` }}>
                      <span style={{ fontSize: 11, color: '#92400e' }}>🔧 Frais mise en place (one-shot)</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e' }}>{fmt(meta.frais_mise_en_place)}</span>
                    </div>
                  )}
                  {/* Mode de paiement */}
                  {meta.payment_method_id && (() => {
                    const pm = paymentMethods.find(m => String(m.id) === String(meta.payment_method_id));
                    return pm ? (
                      <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 8,
                        background: t.primarySoft, border: `1px solid ${t.primaryBorder}` }}>
                        <span style={{ fontSize: 10, color: t.textMuted, display: 'block', marginBottom: 2 }}>Mode de paiement</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: t.primary }}>{pm.libelle}</span>
                        {pm.numero_rib && <span style={{ fontSize: 11, color: t.text }}> · {pm.numero_rib}</span>}
                      </div>
                    ) : null;
                  })()}
                  {/* Jour de paiement */}
                  {meta.jour_paiement > 0 && (
                    <div style={{ marginTop: 6, padding: '5px 10px', borderRadius: 8,
                      background: t.primarySoft, border: `1px solid ${t.primaryBorder}`,
                      display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12 }}>📅</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: t.primary }}>
                        Paiement le {meta.jour_paiement} de chaque mois
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* MÉTA CONTRAT */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px' }}>Détails du contrat</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Field label="Lieu de signature" value={meta.lieu_signature} onChange={v => setMeta(m => ({ ...m, lieu_signature: v }))} placeholder="Pointe-Noire, République Du Congo" theme={t} />
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>Date de signature</label>
                <input type="date" value={meta.date_signature}
                  onChange={e => setMeta(m => ({ ...m, date_signature: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none' }} />
              </div>
            </div>
          </div>
        </div>

        {/* ── PANNEAU DROIT : ARTICLES ── */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
              <BookOpen size={14} style={{ color: t.primary }} /> Articles du contrat
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* Ajouter depuis la bibliothèque */}
              <div style={{ position: 'relative' }}>
                <select
                  defaultValue=""
                  onChange={e => {
                    const tpl = templates.find(t => String(t.id) === e.target.value);
                    if (tpl) { addArticle(tpl); e.target.value = ''; }
                  }}
                  style={{ padding: '8px 12px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textMuted, fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                  <option value="" disabled>+ Depuis la bibliothèque</option>
                  {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.titre.substring(0, 45)}…</option>)}
                </select>
              </div>
              <button onClick={() => addArticle()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: t.primarySoft, color: t.primary, border: `1px solid ${t.primaryBorder}`, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <Plus size={13} /> Article vide
              </button>
            </div>
          </div>

          {articles.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', borderRadius: 14, background: t.bgCard, border: `2px dashed ${t.border}`, color: t.textMuted }}>
              <BookOpen size={30} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
              <p style={{ fontSize: 13 }}>Aucun article. Ajoutez-en un ou chargez depuis la bibliothèque.</p>
            </div>
          ) : (
            articles.map((article, i) => (
              <ArticleCard
                key={i}
                article={article}
                index={i}
                total={articles.length}
                onChange={updated => updateArticle(i, updated)}
                onRemove={() => removeArticle(i)}
                onMoveUp={() => moveArticle(i, -1)}
                onMoveDown={() => moveArticle(i, 1)}
                theme={t}
              />
            ))
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

export default ContratManager;